
import { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { supabase } from "@/integrations/supabase/client";
import { Quote, Customer } from "@/types/crm";

interface QuoteLineItem {
  id: string;
  description: string;
  quantity: number;
  unit_price: number;
  line_total: number;
}

interface QuoteViewProps {
  quote: Quote;
  customer?: Customer;
}

const QuoteView = ({ quote, customer }: QuoteViewProps) => {
  const [lineItems, setLineItems] = useState<QuoteLineItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchLineItems = async () => {
      try {
        const { data, error } = await supabase
          .from('quote_line_items')
          .select('*')
          .eq('quote_id', quote.id)
          .order('created_at');

        if (error) throw error;
        setLineItems(data || []);
      } catch (error) {
        console.error('Error fetching line items:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchLineItems();
  }, [quote.id]);

  if (isLoading) {
    return <div className="text-center p-4">Loading quote details...</div>;
  }

  return (
    <div className="space-y-4 print:p-8 print:bg-white print:text-black print:shadow-none print:break-inside-avoid"
         style={{
           '@media print': {
             pageBreakInside: 'avoid',
             breakInside: 'avoid'
           }
         } as React.CSSProperties}>
      
      {/* Header */}
      <div className="text-center border-b pb-4 print:break-inside-avoid">
        <img 
          src="/lovable-uploads/3cf16c6f-4b71-4a91-91b7-85c354344290.png" 
          alt="Contract Funding Group Logo" 
          className="h-64 mx-auto mb-4 print:h-32"
        />
        <div className="mb-2">
          <h2 className="text-lg font-semibold text-gray-800 print:text-base">Contract Funding Group, LLC</h2>
          <p className="text-sm text-gray-600 print:text-xs">PO Box 85, East Lansing, MI 48826</p>
          <p className="text-sm text-gray-600 print:text-xs">Phone: 517-908-0433</p>
        </div>
        <h1 className="text-2xl font-bold print:text-xl">QUOTE</h1>
        <p className="text-lg text-gray-600 print:text-base">#{quote.quote_number}</p>
        <p className="text-sm text-gray-500 print:text-xs">
          Created: {new Date(quote.created_at).toLocaleDateString()}
          {quote.valid_until && (
            <span> | Valid Until: {new Date(quote.valid_until).toLocaleDateString()}</span>
          )}
        </p>
      </div>

      {/* Quote Info */}
      <div className="grid grid-cols-2 gap-6 print:break-inside-avoid print:gap-4">
        <Card className="print:shadow-none print:border">
          <CardHeader className="print:pb-2">
            <CardTitle className="text-lg print:text-base">Quote Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 print:text-sm">
            <div>
              <strong>Title:</strong> {quote.title}
            </div>
            {quote.description && (
              <div>
                <strong>Description:</strong> {quote.description}
              </div>
            )}
            <div>
              <strong>Status:</strong> 
              <span className="ml-2 px-2 py-1 bg-gray-100 rounded text-sm print:bg-transparent print:border">
                {quote.status}
              </span>
            </div>
          </CardContent>
        </Card>

        <Card className="print:shadow-none print:border">
          <CardHeader className="print:pb-2">
            <CardTitle className="text-lg print:text-base">Customer Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 print:text-sm">
            <div>
              <strong>Name:</strong> {customer?.name || 'Unknown Customer'}
            </div>
            {customer?.email && (
              <div>
                <strong>Email:</strong> {customer.email}
              </div>
            )}
            {customer?.phone && (
              <div>
                <strong>Phone:</strong> {customer.phone}
              </div>
            )}
            {customer?.company && (
              <div>
                <strong>Company:</strong> {customer.company}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Line Items */}
      <Card className="print:shadow-none print:border print:break-inside-avoid">
        <CardHeader className="print:pb-2">
          <CardTitle className="text-lg print:text-base">Quote Items</CardTitle>
        </CardHeader>
        <CardContent>
          <Table className="print:text-sm">
            <TableHeader>
              <TableRow>
                <TableHead className="print:border print:bg-gray-50">Description</TableHead>
                <TableHead className="text-right print:border print:bg-gray-50">Quantity</TableHead>
                <TableHead className="text-right print:border print:bg-gray-50">Unit Price</TableHead>
                <TableHead className="text-right print:border print:bg-gray-50">Total</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {lineItems.map((item) => (
                <TableRow key={item.id}>
                  <TableCell className="print:border">{item.description}</TableCell>
                  <TableCell className="text-right print:border">{item.quantity}</TableCell>
                  <TableCell className="text-right print:border">
                    ${item.unit_price.toFixed(2)}
                  </TableCell>
                  <TableCell className="text-right print:border">
                    ${item.line_total.toFixed(2)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Totals */}
      <Card className="print:shadow-none print:border print:break-inside-avoid">
        <CardContent className="pt-6">
          <div className="space-y-2 text-right print:text-sm">
            <div className="flex justify-between text-lg print:text-base">
              <span>Subtotal:</span>
              <span>${quote.subtotal.toFixed(2)}</span>
            </div>
            {quote.tax_rate && quote.tax_rate > 0 && (
              <div className="flex justify-between">
                <span>Tax ({(quote.tax_rate * 100).toFixed(2)}%):</span>
                <span>${(quote.tax_amount || 0).toFixed(2)}</span>
              </div>
            )}
            <div className="flex justify-between text-xl font-bold border-t pt-2 print:text-lg print:border-black">
              <span>Total:</span>
              <span>${quote.total_amount.toFixed(2)}</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Notes */}
      {quote.notes && (
        <Card className="print:shadow-none print:border print:break-inside-avoid">
          <CardHeader className="print:pb-2">
            <CardTitle className="text-lg print:text-base">Notes</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="whitespace-pre-wrap print:text-sm">{quote.notes}</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default QuoteView;
